// t() reads the page language, which the compiler cannot see, and this stays
// on screen while the language changes.
"use no memo";
import { browserSupportsWebAuthn } from "@simplewebauthn/browser";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import Camera from "~icons/ph/camera";
import CaretLeft from "~icons/ph/caret-left";
import Devices from "~icons/ph/devices";
import Fingerprint from "~icons/ph/fingerprint";
import Key from "~icons/ph/key";
import LinkIcon from "~icons/ph/link";
import Pencil from "~icons/ph/pencil-simple";
import Settings from "~icons/ph/gear-six";
import ShieldCheck from "~icons/ph/shield-check";
import ShieldWarning from "~icons/ph/shield-warning";
import SignOut from "~icons/ph/sign-out";
import type { Availability, AuthenticatedSnapshot } from "../../shared/types";
import { useShell } from "../app/shell";
import { useSetStatus, useSubmitting } from "../data/mutations";
import { sessionsOptions } from "../data/queries";
import { date, number, t } from "../i18n";
import { RecoveryCode } from "../links/guest-account";
import { Avatar, FormError } from "../ui/components";
import { StatusMenu } from "./status-menu";
import { useKeepFocusWhenGone } from "./account-recovery";
import { useSignOut } from "./account-dialogs";
import { NewDeviceCard } from "./new-device-card";
import { AccountRow, RowHead } from "./sign-in-settings";

// Here only while the account has no way back, and until a new code is put away.
// Everything else about signing in stays in Settings.
function WayBackCard({ username }: { username: string }) {
  const { recovery } = useShell();
  const submitting = useSubmitting();
  const passkeys = browserSupportsWebAuthn();
  return (
    <section
      className="way-back-card"
      aria-label={t("لا توجد طريقة للعودة إلى حسابك بعد")}
    >
      {recovery.code ? (
        <AccountRow
          icon={Key}
          title={t("رمز استعادة الحساب")}
          detail={
            <span role="status">
              {t(
                "احتفظ بهذا الرمز في مكان آمن. يتيح لك الدخول من أي جهاز، وإنشاء رمز جديد يلغي السابق.",
              )}
            </span>
          }
        >
          <RecoveryCode code={recovery.code} username={username} />
          <button className="button secondary" onClick={recovery.clear}>
            {t("حفظت الرمز")}
          </button>
        </AccountRow>
      ) : (
        <AccountRow
          icon={ShieldWarning}
          title={t("لا توجد طريقة للعودة إلى حسابك بعد")}
          detail={t(
            "إذا فقدت هذا المتصفح فقدت هذا الحساب. أضف طريقة للعودة إليه.",
          )}
        >
          <div className="way-back-actions">
            {passkeys && (
              <button
                className="button"
                disabled={submitting}
                onClick={() => void recovery.addPasskey()}
              >
                {t("إضافة مفتاح مرور")}
              </button>
            )}
            <button
              className={passkeys ? "button secondary" : "button"}
              disabled={submitting}
              onClick={() => void recovery.save()}
            >
              {t("حفظ رمز الاستعادة")}
            </button>
          </div>
        </AccountRow>
      )}
      <FormError message={recovery.error} />
    </section>
  );
}

function SecurityRow({
  icon,
  title,
  detail,
}: {
  icon: typeof Key;
  title: string;
  detail: string;
}) {
  const shell = useShell();
  return (
    <li>
      <button
        className="account-row-main"
        onClick={() => shell.open({ kind: "sign-in" })}
      >
        <RowHead icon={icon} title={title} detail={detail} />
        <CaretLeft
          className="account-row-caret directional"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>
    </li>
  );
}

function SecurityCard({
  account: { me, passkeys },
}: {
  account: AuthenticatedSnapshot;
}) {
  const sessions = useQuery(sessionsOptions(me.id));
  return (
    <section className="account-card security-card">
      <div className="security-card-heading">
        <h2>{t("الدخول والأمان")}</h2>
        <span className="no-password-badge">
          <ShieldCheck width={14} height={14} aria-hidden="true" />
          {t("لا حاجة لكلمة مرور")}
        </span>
      </div>
      <ul className="account-row-list">
        {(browserSupportsWebAuthn() || passkeys.length > 0) && (
          <SecurityRow
            icon={Fingerprint}
            title={t("مفاتيح المرور")}
            detail={
              passkeys.length
                ? t("المحفوظة: {count}", { count: number(passkeys.length) })
                : t("لم يُضف بعد")
            }
          />
        )}
        <SecurityRow
          icon={Key}
          title={t("رمز الاستعادة")}
          detail={me.has_recovery ? t("محفوظ") : t("غير محفوظ")}
        />
        <SecurityRow
          icon={Devices}
          title={t("الأجهزة المسجّلة")}
          // Holds the row's height until the list arrives.
          detail={
            sessions.data
              ? t("النشطة: {count}", { count: number(sessions.data.length) })
              : "\u00a0"
          }
        />
      </ul>
    </section>
  );
}

export function ProfileScreen({ account }: { account: AuthenticatedSnapshot }) {
  const { me } = account;
  const shell = useShell();
  const { recovery } = shell;
  const setStatus = useSetStatus();
  const signOut = useSignOut();
  const submitting = useSubmitting();
  const [error, setError] = useState("");
  // A recovery code is shown only until the account page is left.
  const { clear } = recovery;
  useEffect(() => clear, [clear]);
  const showWayBack = !recovery.canReturn || !!recovery.code;
  useKeepFocusWhenGone(showWayBack);
  async function changeStatus(status: Availability) {
    setError("");
    try {
      await setStatus.mutateAsync(status);
    } catch (e) {
      setError((e as Error).message);
    }
  }
  async function leave() {
    try {
      await signOut();
    } catch (e) {
      setError((e as Error).message);
    }
  }
  return (
    <main
      id="main-content"
      tabIndex={-1}
      className="app-screen account-screen"
      aria-label={t("حسابك")}
    >
      <div className="account-column">
        <div className="screen-heading">
          <h1>{t("حسابك")}</h1>
          <button
            className="button secondary account-settings"
            onClick={() => shell.open({ kind: "settings" })}
          >
            <Settings width={18} height={18} aria-hidden="true" />
            <span>{t("الإعدادات")}</span>
          </button>
        </div>
        <section className="account-card account-identity">
          <button
            className="avatar-button avatar-edit"
            aria-label={t("تغيير صورة الحساب")}
            title={t("تغيير صورة الحساب")}
            onClick={() => shell.open({ kind: "avatar" })}
          >
            <Avatar person={me} large dot />
            <span className="avatar-edit-badge" aria-hidden="true">
              <Camera width={14} height={14} />
            </span>
          </button>
          <div className="account-identity-text">
            <div className="account-name">
              <h2>{me.name}</h2>
              <p className="profile-username">
                <bdi dir="ltr">@{me.username}</bdi>
              </p>
            </div>
            {me.bio && (
              <p className="profile-bio" dir="auto">
                {me.bio}
              </p>
            )}
            <StatusMenu
              status={me.status}
              busy={submitting}
              onChange={(status) => void changeStatus(status)}
            />
          </div>
          <div className="account-identity-actions">
            <button
              className="button secondary"
              onClick={() => shell.open({ kind: "edit" })}
            >
              <Pencil width={16} height={16} aria-hidden="true" />
              {t("تعديل الحساب")}
            </button>
            <button
              className="button secondary"
              onClick={() => shell.open({ kind: "add-friend", step: "share" })}
            >
              <LinkIcon width={16} height={16} aria-hidden="true" />
              {t("مشاركة الحساب")}
            </button>
          </div>
        </section>
        {showWayBack && <WayBackCard username={me.username} />}
        <div className="account-panels">
          <NewDeviceCard />
          <SecurityCard account={account} />
        </div>
        <FormError message={error} />
        <div className="account-footer">
          {me.created_at && (
            <p>{t("عضو منذ {date}", { date: date(me.created_at) })}</p>
          )}
          <button
            className="logout-link"
            disabled={submitting}
            onClick={() => {
              // With no way back, signing out loses the account.
              if (recovery.canReturn) void leave();
              else shell.open({ kind: "logout" });
            }}
          >
            <SignOut width={16} height={16} aria-hidden="true" />
            {t("تسجيل الخروج")}{" "}
          </button>
        </div>
      </div>
    </main>
  );
}
